import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import config, { BASE_URL } from "config";
import { AnalyticsConst, AnalyticsInstance, RootState } from "gyg_common";
import { SearchTextInput } from "gyg_common/components";
import deliveryWithUber from "gyg_common/components/assets/icons/services/delivery_with_uber_icon.png";
import navigateIcon from "gyg_common/components/assets/icons/services/navigate_icon.png";
import questionImg from "gyg_common/components/assets/images/illustration_error_question.png";
import Divider from "gyg_common/components/BaseComponents/Divider";
import ModalWithButton from "gyg_common/components/modals/ModalWithButton";
import RecentAddressView from "gyg_common/components/OrderSetup/DeliveryRecentAddress";
import {
  OrderSetupGlobalState,
  SetupScreenComponent,
} from "gyg_common/components/OrderSetup/OrderSetupFlowContent";
import { Spacing } from "gyg_common/components/styles/number";
import {
  AddressAutocompleteSuggestion,
  DeliveryAddress,
  DeliveryModalState,
} from "gyg_common/redux_store/order/models";
import { getRecentAddresses } from "gyg_common/services/api/order";
import {
  getCurrentLocationPermission,
  getUserLocation,
  requestLocationPermission,
} from "modules/location";
import { Typography } from "styles/typography";

import { LocationPermissionModal } from "../Location/LocationPermissionModal";
import { FlyingBurritoLoader } from "../shared/Loading/FlyingBurritoLoader/FlyingBurritoLoader";

import colours from "@/styles/colours";

const GOOGLE_IP_ADDRESS_GEOCODE_URL = `${config.api.host}/deliveries/addresses/geocode`;
const DEBOUNCE_TIMEOUT = 1500;

const styles = StyleSheet.create({
  container: { paddingHorizontal: Spacing.Regular },
  deliveryWithUberContainer: {
    width: "100%",
    alignItems: "center",
  },
  deliveryWithUberIcon: { height: 50, width: 120 },
  navigateIcon: {
    height: Spacing.Light,
    width: Spacing.Regular,
    marginRight: Spacing.Thin,
  },
  loadingContainer: {
    margin: Spacing.MediumThick,
    display: "flex",
    justifyContent: "center",
  },
  useMyLocationText: { ...Typography.bodyBold, color: colours.black },
  recentAddressesText: { ...Typography.bodyBold, color: colours.black },
  recentAddressesContainer: { paddingVertical: Spacing.Regular },
  resultsContainer: {
    position: "absolute",
    left: Spacing.Regular,
    right: Spacing.Regular,
    top: 55,
    backgroundColor: "white",
  },
  useMyLocationButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: Spacing.Regular,
  },
});

const Screen: React.FC<SetupScreenComponent<DeliveryModalState>> = ({
  state,
  onNext,
}) => {
  const { currentUser } = useSelector((s: RootState) => s.login);
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>(
    state.DropoffAddress?.fullAddress ?? ""
  );
  const [isFocused, setFocused] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRecentAddressesLoading, setIsRecentAddressesLoading] =
    useState<boolean>(false);
  const [isLoadingMyLocation, setIsLoadingMyLocation] =
    useState<boolean>(false);
  const isShowLoadingAnimation =
    isLoading || isLoadingMyLocation || isRecentAddressesLoading;
  const [autoCompleteResult, setAutoCompleteResult] = useState<
    AddressAutocompleteSuggestion[]
  >(state.AutoCompleteResult ?? []);

  const [recentAddresses, setRecentAddresses] = useState<DeliveryAddress[]>(
    OrderSetupGlobalState.getInstance().recentAddresses ?? []
  );

  const [debounce, setDebounce] = useState<NodeJS.Timeout>();
  const [sessionToken, setSessionToken] = useState<string>(uuidv4());

  const [isLocationPermissionModalVisible, setLocationPermissionModalVisible] =
    useState<boolean>(false);
  const [shareLocationModalVisible, setShareLocationModalVisible] =
    useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      setFocused(true);
    }, 100);
  }, []);

  useEffect(() => {
    AnalyticsInstance.trackView({
      page_name: AnalyticsConst.Events.Address,
      page_type: AnalyticsConst.Events.Address + "_view",
    });
  });

  useEffect(() => {
    if (currentUser && !OrderSetupGlobalState.getInstance().recentAddresses) {
      setIsRecentAddressesLoading(true);
      getRecentAddresses()
        .then((addresses) => {
          OrderSetupGlobalState.getInstance().recentAddresses = addresses ?? [];
          setRecentAddresses(addresses);
        })
        .finally(() => {
          setIsRecentAddressesLoading(false);
        });
    }
  }, [currentUser]);

  const getAddressAutocomplete = useCallback(
    async (address: string): Promise<AddressAutocompleteSuggestion[]> => {
      const url = `${BASE_URL}/deliveries/addresses/autocomplete?address=${address}&session_token=${sessionToken}&suggestions_only=true`;
      const response = await fetch(url);
      return await response.json();
    },
    [sessionToken]
  );

  const getAddressDetails = useCallback(
    async (googlePlaceId: string): Promise<DeliveryAddress> => {
      const url = `${BASE_URL}/deliveries/addresses/details?google_place_id=${googlePlaceId}&session_token=${sessionToken}`;
      const response = await fetch(url);
      // once session token has concluded, the token is no longer valid. we need to generate a new one
      setSessionToken(uuidv4());
      return await response.json();
    },
    [sessionToken]
  );

  const updateDropoffAddress = useCallback(
    async (
      suggestion: AddressAutocompleteSuggestion,
      result: AddressAutocompleteSuggestion[] = []
    ) => {
      const isSameAddress =
        state.DropoffAddress?.googlePlaceId === suggestion.googlePlaceId;

      if (isSameAddress) {
        onNext({
          ...state,
          AutoCompleteResult: result,
        });
      } else {
        const dropoffAddress = await getAddressDetails(
          suggestion.googlePlaceId
        );
        onNext({
          ...state,
          DropoffAddress: dropoffAddress,
          Apartment: dropoffAddress.suiteAndFloor,
          Business: dropoffAddress.name,
          DropOffOption: undefined,
          DropOffOptionTitle: undefined,
          Instructions: undefined,
          AutoCompleteResult: result,
        });
      }
    },
    [state, getAddressDetails, onNext]
  );

  const handleAddressSearch = useCallback(
    (text: string, autoSelectFirst?: boolean) => {
      clearTimeout(debounce);
      setDebounce(
        setTimeout(async () => {
          setIsLoading(true);

          if (text.length <= 0) {
            return;
          }

          const results = await getAddressAutocomplete(text);

          if (autoSelectFirst && results.length > 0) {
            const suggestion = results[0];
            updateDropoffAddress(suggestion, results);
          }

          setAutoCompleteResult(results);
          setIsLoading(false);
          setIsLoadingMyLocation(false);
        }, DEBOUNCE_TIMEOUT)
      );
    },
    [debounce, getAddressAutocomplete, updateDropoffAddress]
  );

  const handleChangeText = (text: string) => {
    setSearchText(text);
    if (text.length > 2 || text === "") {
      handleAddressSearch(text);
    } else {
      setAutoCompleteResult([]);
    }
  };

  const getAddressFromCoordinates = (
    latitude: number,
    longitude: number
  ): Promise<DeliveryAddress> => {
    return new Promise((resolve, reject) => {
      fetch(
        `${GOOGLE_IP_ADDRESS_GEOCODE_URL}?latitude=${latitude}&longitude=${longitude}`
      )
        .then((response) => response.json())
        .then((responseJson) => {
          if (responseJson) {
            resolve(responseJson);
          } else {
            reject("not found");
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const askForLocationPermission = useCallback(async () => {
    const res = await requestLocationPermission();
    if (!res.userLocation) {
      getCurrentLocationPermission()
        .then(async (val) => {
          if (val === "denied") {
            setLocationPermissionModalVisible(false);
            setShareLocationModalVisible(true);
          } else {
            setShareLocationModalVisible(false);
            setLocationPermissionModalVisible(true);
          }
          setIsLoadingMyLocation(false);
        })
        .catch(() => {});
    } else {
      setShareLocationModalVisible(false);
      setLocationPermissionModalVisible(false);

      getUserLocation()
        .then((data) => {
          if (data) {
            const coordinates = {
              longitude: data.coords.longitude,
              latitude: data.coords.latitude,
            };
            getAddressFromCoordinates(
              coordinates.latitude,
              coordinates.longitude
            )
              .then((address) => {
                if (address) {
                  setSearchText(address.fullAddress);
                  const isSameAddress =
                    state.DropoffAddress?.googlePlaceId ===
                    address.googlePlaceId;

                  onNext({
                    ...state,
                    DropoffAddress: address,
                    Apartment: !isSameAddress
                      ? address.suiteAndFloor
                      : state.Apartment,
                    Business: !isSameAddress ? address.name : state.Business,
                  });
                } else {
                  setIsLoadingMyLocation(false);
                }
              })
              .catch(() => {
                setIsLoadingMyLocation(false);
              });
          }
        })
        .catch(() => {
          setIsLoadingMyLocation(false);
        });
    }
  }, [state, onNext]);

  const useMyLocationPress = () => {
    setIsLoadingMyLocation(true);
    askForLocationPermission();
  };

  const onRecentAddressPress = (address: DeliveryAddress) => {
    onNext({
      ...state,
      DropoffAddress: address,
      Apartment: address.suiteAndFloor,
      Business: address.name,
      AutoCompleteResult: [address],
    });
  };

  return (
    <View style={styles.container}>
      <SearchTextInput
        placeHolder={t("OrderManagement:searchDeliveryAddressPlaceholder")}
        onClear={() => {
          setSearchText("");
          setAutoCompleteResult([]);
        }}
        onSearch={() => {}}
        isFocused={isFocused}
        setFocused={setFocused}
        onChangeText={handleChangeText}
        value={searchText}
      />

      <Pressable
        onPress={useMyLocationPress}
        style={styles.useMyLocationButtonContainer}>
        <Image
          resizeMode='contain'
          source={navigateIcon as ImageSourcePropType}
          style={styles.navigateIcon}
        />
        <Text style={styles.useMyLocationText}>
          {t("OrderManagement:useMyLocation")}
        </Text>
      </Pressable>

      <Divider />

      {!searchText && recentAddresses.length > 0 && !isShowLoadingAnimation && (
        <View style={styles.recentAddressesContainer}>
          <Text style={styles.recentAddressesText}>
            {t("OrderManagement:recentAddresses")}
          </Text>
          {recentAddresses.map((address, i) => {
            return (
              <RecentAddressView
                onPress={onRecentAddressPress}
                key={"recent-address-" + i}
                recentAddress={address}
                hideDivider={
                  recentAddresses.length === 1 ||
                  recentAddresses.length === i + 1
                }
              />
            );
          })}
        </View>
      )}

      <View style={styles.deliveryWithUberContainer}>
        <Image
          resizeMode='contain'
          source={deliveryWithUber as ImageSourcePropType}
          style={styles.deliveryWithUberIcon}
        />
      </View>

      <View style={styles.resultsContainer}>
        {isShowLoadingAnimation && (
          <div style={styles.loadingContainer}>
            <FlyingBurritoLoader />
          </div>
        )}
        {!isShowLoadingAnimation && autoCompleteResult.length > 0 && (
          <ul id='autocomplete-results'>
            {autoCompleteResult.map((result) => (
              <li
                key={result.googlePlaceId}
                onClick={() => {
                  updateDropoffAddress(result, autoCompleteResult);
                }}>
                {result.fullAddress}
              </li>
            ))}
          </ul>
        )}
      </View>

      <ModalWithButton
        image={questionImg as ImageSourcePropType}
        modalId='LocationPermissionModal'
        isVisible={isLocationPermissionModalVisible}
        primaryButton={{
          name: "CLOSE",
          action: () => setLocationPermissionModalVisible(false),
        }}
        small={true}
        title={t("Dashboard:turnLocationOn")}
        message={t("Dashboard:locationDeniedMessage")}
        onModalClose={() => setLocationPermissionModalVisible(false)}
      />
      <LocationPermissionModal
        isVisible={shareLocationModalVisible}
        onClose={() => setShareLocationModalVisible(false)}
        onPrimaryClick={askForLocationPermission}
      />
    </View>
  );
};

export default Screen;
